'use client';
import { useCallback, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { getLatestArticlesClient } from '@/lib/api/article/latestArticle/LatestArticleClient';
import { categoryArticlesClient } from '@/lib/api/article/categoryArticle/CategoryArticleClient';
import SvgLeftCaret from '@/components/Icons/SvgLeftCaret';
import SvgRightCaret from '@/components/Icons/SvgRightCaret';
import { getPaginationWindow } from '@/utils/articles/pagination';

const PAGE_SIZE = 6;

/**
 * Displays a paginated list of article previews based on the selected category.
 *
 * [ROY] The parameter `articlesGroupedByCategory` has the initial articles
 * for each category passed in, and building that data uses more fetches than
 * we probably need. We could optimize this further and turn it into a static
 * rendered page unless we need something dynamic.
 */
export default function ArticlePreviews({
  categories,
  selectedCategory,
  articlesGroupedByCategory,
  priorityArticles
}) {
  // The first page of articles for the selected category passed in from props
  const selectedArticles = articlesGroupedByCategory?.find(
    (item) => item.category === selectedCategory
  );

  // Cache of fetched pages per category so re-visiting a page is instant:
  // { category: { [pageNumber]: [articles] } }
  const [pagesByCategory, setPagesByCategory] = useState({});
  const [currentPageByCategory, setCurrentPageByCategory] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const loadingRef = useRef(false);
  const sectionRef = useRef(null);

  const currentPage = currentPageByCategory[selectedCategory] ?? 1;
  const cachedPages = useMemo(
    () => pagesByCategory[selectedCategory] ?? {},
    [pagesByCategory, selectedCategory]
  );

  const firstCategory = categories[0].category_slug;
  const priorityArticleCount = selectedArticles?.priorityArticlesSkip || 0;
  const totalArticleCount =
    (selectedArticles?.articleCount || 0) + priorityArticleCount;
  const totalPages = Math.max(Math.ceil(totalArticleCount / PAGE_SIZE), 1);

  const fetchPage = useCallback(
    async (pageNum) => {
      if (!selectedArticles) return;

      const articlesBeforePage = PAGE_SIZE * (pageNum - 1);

      let loadedArticles;
      if (selectedCategory === firstCategory) {
        loadedArticles = await getLatestArticlesClient(
          articlesBeforePage - priorityArticles.length,
          PAGE_SIZE
        );
      } else {
        const categoryArticles = await categoryArticlesClient(
          selectedArticles.apiCategory,
          articlesBeforePage - (selectedArticles.priorityArticlesSkip || 0),
          PAGE_SIZE
        );
        loadedArticles = categoryArticles?.articles || [];
      }

      setPagesByCategory((prev) => ({
        ...prev,
        [selectedCategory]: {
          ...(prev[selectedCategory] || {}),
          [pageNum]: loadedArticles
        }
      }));
    },
    [firstCategory, priorityArticles.length, selectedArticles, selectedCategory]
  );

  const goToPage = useCallback(
    async (pageNum) => {
      if (
        pageNum === currentPage ||
        pageNum < 1 ||
        pageNum > totalPages ||
        loadingRef.current
      ) {
        return;
      }

      if (pageNum !== 1 && !cachedPages[pageNum]) {
        loadingRef.current = true;
        setIsLoading(true);
        try {
          await fetchPage(pageNum);
        } catch (error) {
          // Most likely just a network error. Can show toast here if worthwhile.
          console.error(
            `Could not load page ${pageNum} for category ${selectedCategory}:`,
            error
          );
          return;
        } finally {
          loadingRef.current = false;
          setIsLoading(false);
        }
      }

      setCurrentPageByCategory((prev) => ({
        ...prev,
        [selectedCategory]: pageNum
      }));
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    },
    [cachedPages, currentPage, fetchPage, selectedCategory, totalPages]
  );

  const articles =
    currentPage === 1
      ? selectedArticles?.articles || []
      : cachedPages[currentPage] || [];

  return (
    <section
      ref={sectionRef}
      className="blog-previews max-w-container mx-auto w-full px-4 pt-11 pb-20 sm:px-8 lg:px-0"
    >
      <div className="grid-rows-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {selectedArticles ? (
          articles.map((article, index) => (
            <div key={index}>
              <ArticlePreviewCard article={article} />
            </div>
          ))
        ) : (
          <div className="body-r block justify-center">
            <div>Loading articles...</div>
          </div>
        )}
      </div>
      {selectedArticles && totalPages > 1 && (
        <>
          <hr className="mt-8 border-t border-gray-200 pb-2" />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            isLoading={isLoading}
            onPageChange={goToPage}
          />
        </>
      )}
    </section>
  );
}

const PAGE_WINDOW_SIZE = 6;

const Pagination = ({ currentPage, totalPages, isLoading, onPageChange }) => {
  const pageNumbers = getPaginationWindow(
    currentPage,
    totalPages,
    PAGE_WINDOW_SIZE
  );

  return (
    <nav
      aria-label="Article pagination"
      className="mt-10 flex items-center justify-center gap-2"
    >
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || isLoading}
        className="body-r px-3 py-2 disabled:opacity-40"
      >
        <SvgLeftCaret />
      </button>
      {pageNumbers.map((pageNum) => (
        <button
          key={pageNum}
          type="button"
          onClick={() => onPageChange(pageNum)}
          disabled={isLoading}
          aria-current={pageNum === currentPage ? 'page' : undefined}
          className={`body-r h-10 w-10 disabled:opacity-40 ${
            pageNum === currentPage
              ? 'bg-parlor-green-10'
              : 'hover:bg-parlor-green hover:text-white'
          }`}
        >
          {pageNum}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || isLoading}
        className="body-r px-3 py-2 disabled:opacity-40"
      >
        <SvgRightCaret />
      </button>
    </nav>
  );
};

export const ArticlePreviewCard = ({ article }) => {
  const { url, feature_image, title, categoryConnection } = article ?? {};
  if (!url || !feature_image || !title || !categoryConnection) {
    console.warn('Article Preview is missing required fields:', article);
    return null;
  }

  return (
    <div className="p-4">
      <div className="mt-4 mb-4 lg:mb-6">
        {feature_image.url && (
          <Link
            href={url}
            prefetch={false}
            className="relative block h-60.75 w-full overflow-hidden"
          >
            <Image
              src={feature_image.url}
              fill
              sizes="(min-width: 1280px) 368px, (min-width: 1024px) 310px, (min-width: 768px) 328px, (min-width: 640px) 264px, 607px"
              alt={
                feature_image.title?.replace(/\.[^/.]+$/, '') ||
                `${title} Featured Image`
              }
              className="cursor-pointer bg-white object-cover transition delay-100 ease-in-out hover:opacity-60"
            />
          </Link>
        )}
      </div>
      <p className="preheading-s text-parlor-green pb-2 lg:pb-4">
        {categoryConnection.title}
      </p>
      <h2 className="h5 hover:text-cozy-cognac text-black">
        <Link href={url} prefetch={false}>
          {title}
        </Link>
      </h2>
    </div>
  );
};
